import api from 'services/api';
import { REJECT, array, bool, mapReceived, nullable, num, numeric, object, received, str, type Received } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// POST /events/instance_status (routes/events.js). Given a set of event_ids it returns one status
// row per id (an event_id lives in only one instance table, so the backend merges the process/file
// aggregates by COALESCE):
//   - status_active: a raised process instance or an unprocessed file instance exists for the event.
//   - status_total: COUNT(*) of instances (Postgres serializes the bigint as a string, hence numeric()).
//   - status_last_at: when the active state most recently began, or null.
//
// This is the validated wire shape (snake). The camelCase, keyed-by-id projection is the
// `getEventInstanceStatus` operation.
export interface EventInstanceStatusWire {
  readonly event_id: number;
  readonly status_active: boolean;
  readonly status_total: number;
  readonly status_last_at: string | null;
}

const statusWireSchema = object<EventInstanceStatusWire>({
  event_id: num(REJECT),
  status_active: bool(),
  status_total: numeric(),
  status_last_at: nullable(str()),
});

interface StatusResponseWire {
  readonly statuses: readonly EventInstanceStatusWire[];
}

const responseSchema = object<StatusResponseWire>({
  statuses: array(statusWireSchema),
});

/**
 * Fetch instance-status rows for a set of event definitions. Returns `[]` for an empty input (no
 * request made). Throws only if the response envelope is structurally malformed; rows whose
 * `event_id` drifts are dropped individually.
 */
export const getEventInstanceStatuses = async (eventIds: readonly number[]): Promise<Received<readonly EventInstanceStatusWire[]>> => {
  if (eventIds.length === 0) return received([]);

  const res = await api.post('/events/instance_status', { event_ids: eventIds });
  return mapReceived(readResponse(res.data, { schema: responseSchema, source: 'getEventInstanceStatuses' }), (wire) => wire.statuses);
};
